import { Fragment, useMemo } from 'react';
import { Settings } from 'lucide-react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { createSearchParams, useNavigate } from 'react-router';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow, TableFooter } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  agreementTemplateOptionsRichDictionary,
  legalContextMap,
  releaseEventsMap,
  jurisdictionMap,
  liabilityMap,
} from 'src/containers/Escrow/Agreement';
import { AgreementConditionEnum, LegalContextEnum, LiabilityEnum } from 'src/graphql/schema';
import { cn } from 'src/lib/utils';
import { Badge } from '@/components/ui/badge';

import NeedChangesBanner from './NeedChangesBanner';

type IConfigurationData = {
  label: string;
  value: React.ReactNode;
};

const configColumnHelper = createColumnHelper<IConfigurationData>();

const configColumns = [
  configColumnHelper.accessor('label', {
    header: '',
    cell: (info) => <div className="font-semibold">{info.getValue()}</div>,
  }),
  configColumnHelper.accessor(() => ':', {
    id: 'separator',
    header: '',
    cell: () => ':',
  }),
  configColumnHelper.accessor('value', {
    header: '',
    cell: (info) => info.getValue(),
  }),
];

const Configuration = () => {
  const {
    softwareEscrow: { id: softwareEscrowId, agreement, currentCompanyRole, startedBy },
  } = useSoftwareEscrow();
  const navigate = useNavigate();
  const {
    icon: Icon,
    label,
    description,
  } = agreementTemplateOptionsRichDictionary[String(agreement?.thirdPartyAgreement)];

  const data = useMemo(
    () => [
      {
        label: 'Agreement template',
        value: (
          <div className="flex items-center gap-2 min-w-[250px]">
            <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <Icon className="size-4 text-foreground" />
            </div>
            <div>
              <div className="font-medium text-sm">{label}</div>
              <div className="text-xs text-foreground/80">{description}</div>
            </div>
          </div>
        ),
      },
    ],
    [Icon, label, description],
  );

  const table = useReactTable({
    data,
    columns: configColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const onEdit = () => {
    navigate({
      pathname: `/software-escrows/onboarding/${softwareEscrowId}/agreement-setup`,
      search: createSearchParams({
        isEdit: 'true',
      }).toString(),
    });
  };

  const isInvitedParty = currentCompanyRole !== startedBy;

  return (
    <div className="space-y-4">
      <div className="text-base font-bold text-foreground">Configuration</div>
      <div className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      cell.column.id === 'separator' && 'w-[85px]',
                      cell.column.id === 'label' && 'w-[190px]',
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          {!isInvitedParty && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={3}>
                  <div className="flex justify-end">
                    <Button variant="outline" size="sm" onClick={onEdit}>
                      Edit
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
};

type IDetailsData = {
  label: string;
  value: React.ReactNode;
};

const columnHelper = createColumnHelper<IDetailsData>();

const columns = [
  columnHelper.accessor('label', {
    header: '',
    cell: (info) => <div className="font-semibold">{info.getValue()}</div>,
  }),
  columnHelper.accessor(() => ':', {
    id: 'separator',
    header: '',
    cell: () => ':',
  }),
  columnHelper.accessor('value', {
    header: '',
    cell: (info) => info.getValue(),
  }),
];

const Details = () => {
  const {
    softwareEscrow: { id: softwareEscrowId, agreement, currentCompanyRole, startedBy },
  } = useSoftwareEscrow();
  const navigate = useNavigate();

  const data = useMemo(() => {
    const tableData: IDetailsData[] = [];

    if (agreement?.legalContext?.length) {
      tableData.push({
        label: 'Legal context',
        value: (
          <Table>
            <TableBody>
              {agreement.legalContext.map((context) => {
                const { icon: Icon, title, description } = legalContextMap[context];

                return (
                  <Fragment key={context}>
                    <TableRow className="border-b-0 group/legal-context">
                      <TableCell className="flex items-center gap-2 pt-0 px-0 group-last/legal-context:pb-0">
                        <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <Icon className="size-4 text-foreground" />
                        </div>
                        <div className="grow-1">
                          <div className="font-medium text-sm">{title}</div>
                          {context !== LegalContextEnum.Other && (
                            <div className="text-xs text-foreground/80">{description}</div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {context === LegalContextEnum.Other && (
                      <TableRow className="border-b-0 group/legal-context">
                        <TableCell className="flex items-center gap-2 pt-0 px-0 group-last/legal-context:pb-0">
                          <div className="size-10 shrink-0" />
                          <div>
                            <div className="font-semibold text-xs">Your agreement title</div>
                            <div className="text-xs text-foreground/80">{agreement.legalContextOther}</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        ),
      });
    }

    if (agreement?.conditions?.length) {
      tableData.push({
        label: 'Release events',
        value: (
          <Table>
            <TableBody>
              {agreement.conditions.map((condition) => {
                const { icon: Icon, title, description } = releaseEventsMap[condition];
                return (
                  <Fragment key={condition}>
                    <TableRow className="border-b-0 group/release-event">
                      <TableCell className="flex items-center gap-2 pt-0 px-0 group-last/release-event:pb-0 min-w-[250px]">
                        <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <Icon className="size-4 text-foreground" />
                        </div>
                        <div className="grow-1">
                          <div className="font-medium text-sm">{title}</div>
                          {condition !== AgreementConditionEnum.Other && (
                            <div className="text-xs text-foreground/80">{description}</div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {condition === AgreementConditionEnum.Other && (
                      <TableRow className="border-b-0 group/release-event">
                        <TableCell className="flex items-center gap-2 pt-0 px-0 group-last/release-event:pb-0">
                          <div className="size-10 shrink-0" />
                          <div>
                            <div className="font-semibold text-xs">Your release events</div>
                            <div className="text-xs text-foreground/80">{agreement.conditionsOther}</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        ),
      });
    }

    if (agreement?.jurisdiction) {
      tableData.push({
        label: 'Jurisdiction',
        value: jurisdictionMap[agreement.jurisdiction],
      });
    }

    if (agreement?.liability) {
      tableData.push({
        label: 'Escrow liability SLA',
        value: (
          <div className="flex items-center gap-8">
            {liabilityMap[agreement.liability]}
            {agreement.liability !== LiabilityEnum.Total_0 && <Badge>Add-on</Badge>}
          </div>
        ),
      });
    }

    return tableData;
  }, [agreement]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Only show details if the agreement is not a third party agreement
  if (agreement?.thirdPartyAgreement) return null;

  const onEdit = () =>
    navigate({
      pathname: `/software-escrows/onboarding/${softwareEscrowId}/agreement-details`,
      search: createSearchParams({
        isEdit: 'true',
      }).toString(),
    });

  const isInvitedParty = currentCompanyRole !== startedBy;

  return (
    <div className="space-y-4">
      <div className="text-base font-bold text-foreground">Details</div>
      <div className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      cell.column.id === 'separator' && 'w-[85px]',
                      cell.column.id === 'label' && 'w-[190px]',
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          {!isInvitedParty && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={3}>
                  <div className="flex justify-end">
                    <Button variant="outline" size="sm" onClick={onEdit}>
                      Edit
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
};

const AgreementSettingsCard = () => {
  const {
    softwareEscrow: { currentCompanyRole, startedBy },
  } = useSoftwareEscrow();

  const isInvitedParty = currentCompanyRole !== startedBy;

  return (
    <Card>
      <CardHeader className="relative">
        {isInvitedParty && (
          <span className="absolute top-6 right-6 italic text-xs text-gray-400">Non-editable fields</span>
        )}
        <CardTitle className="flex items-center gap-2 text-xl">
          <Settings className="size-6" />
          Agreement settings
        </CardTitle>
        <CardDescription>The key terms and legal structure of your escrow agreement.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {isInvitedParty && <NeedChangesBanner />}
        <Configuration />
        <Details />
      </CardContent>
    </Card>
  );
};

export default AgreementSettingsCard;
